import { useState, useCallback } from 'react';

/**
 * Hook for accepting hiring offers
 * @param {Object} options - Configuration options
 * @param {number} options.hiringId - Hiring ID
 * @param {string|Date} options.startDate - Optional start date from job offer
 * @param {string|Date} options.endDate - Optional end date from job offer
 * @param {Function} options.onSuccess - Optional callback called after successful accept (receives result data)
 * @param {Function} options.onError - Optional callback called on error (receives error message)
 * @returns {Object} Accept hiring state and handler function
 */
const useAcceptHiring = ({ hiringId, startDate, endDate, onSuccess, onError }) => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = useCallback(async () => {
    if (!hiringId) {
      const errorMsg = 'No se puede aceptar: falta el ID de la oferta';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return { success: false, error: errorMsg };
    }

    setIsAccepting(true);
    setError(null);

    try {
      // Prepare request body with dates if provided
      const requestBody = {};
      if (startDate) {
        requestBody.startDate = startDate instanceof Date ? startDate.toISOString() : startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate instanceof Date ? endDate.toISOString() : endDate;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/hirings/${hiringId}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        const result = { success: true, data };
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'No se pudo aceptar la oferta';
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Error accepting hiring offer:', error);
      const errorMessage = 'Error al aceptar la oferta';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsAccepting(false);
    }
  }, [hiringId, startDate, endDate, onSuccess, onError]);

  return {
    handleAccept,
    isAccepting,
    error
  };
};

export default useAcceptHiring;

